import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DeviceSettingsFeatureToggle } from 'src/app/enums/feature-toggle.enum';
import { SectionPresenter } from 'src/app/presenters/models/section-presenter';
import { FeatureToggleService } from 'src/app/services/feature-toggle/feature-toggle.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { FnCtrlKeySwapFeature } from './fn-ctrl-key-swap/fn-ctrl-key-swap-feature';
import { KeyboardBacklightFeature } from './keyboard-backlight/keyboard-backlight-feature';
import { KeyboardMapFeature } from './keyboard-map/keyboard-map-feature';
import { PadControlLinkFeature } from './pad-control-links-feature/pad-control-links-feature';
import { TopRowFunctionFeature } from './top-row-function/top-row-function-feature';
import { UserDefinedKeyFeature } from './user-defined-key/user-defined-key-feature';
import { VoipHotkeyFunctionFeature } from './voip-hotkey/voip-hotkey-function-feature';

@Injectable({
	providedIn: 'root',
})
export class IntelligentKeyboardSection extends SectionPresenter {
	constructor(
		private featureToggleService: FeatureToggleService,
		private topRowFunctionFeature: TopRowFunctionFeature,
		private keyboardBacklightFeature: KeyboardBacklightFeature,
		private voipHotkeyFeature: VoipHotkeyFunctionFeature,
		private keyboardMapFeature: KeyboardMapFeature,
		private fnCtrlKeySwapFeature: FnCtrlKeySwapFeature,
		private userDefinedKey: UserDefinedKeyFeature,
		private padControlLinksFeature: PadControlLinkFeature,
		logger: LoggerService
	) {
		super(logger);
	}

	protected isFeatureToggleEnabled(): boolean {
		return this.featureToggleService.isDeviceSettingsFeatureAvailable(
			DeviceSettingsFeatureToggle.InputAndAccessoriesIntelligentKeyboard
		);
	}

	protected initializeLoadedResult() {
		return {
			topRowFunctionFeature: false,
			keyboardBacklightFeature: false,
			voipHotkeyFeature: false,
			keyboardMapFeature: false,
			fnCtrlKeySwapFeature: false,
			userDefinedKey: false,
			padControlLinksFeature: false,
		};
	}

	protected checkFeaturesCapabilities(): Observable<{ value: boolean; type: string }> {
		const observable = new Observable<{ value: boolean; type: string }>((observer) => {
			const next = (value: boolean, type: string) => observer?.next({ value, type });

			this.checkTopRowFunctionFeatureCapability(next);
			this.checkKeyboardBacklightFeatureCapability(next);
			this.checkVoipHotkeyFeatureCapability(next);
			this.checkKeyboardMapFeatureCapability(next);
			this.checkFnCtrlKeySwapFeature(next);
			this.checkUserDefinedKeyFeature(next);
			this.checkPadControlLinksFeature(next);
		});

		return observable;
	}

	private checkPadControlLinksFeature(next: (value: boolean, type: string) => void) {
		this.logger.info(
			'PadControlLinksFeature:handleHasAnyFeatureCapability:padControlLinksFeature:Before'
		);
		this.padControlLinksFeature.getCapabililty().then((value) => {
			this.logger.info(
				'PadControlLinksFeature:handleHasAnyFeatureCapability:padControlLinksFeature:getCapabililty.then',
				value
			);
			const type = 'padControlLinksFeature';
			next(value, type);
		});
	}

	private checkKeyboardMapFeatureCapability(next) {
		this.logger.info(
			'IntelligentKeyboardSection:handleHasAnyFeatureCapability:padControlLinksFeature:Before'
		);
		this.keyboardMapFeature.getCapabililty().then((value) => {
			this.logger.info(
				'IntelligentKeyboardSection:handleHasAnyFeatureCapability:keyboardMapFeature:getCapabililty.then',
				value
			);
			const type = 'keyboardMapFeature';
			next(value, type);
		});
	}

	private checkVoipHotkeyFeatureCapability(next) {
		this.logger.info('IntelligentKeyboardSection:checkVoipHotkeyFeatureCapability:Before');
		this.voipHotkeyFeature.getCapabililty().then((value) => {
			this.logger.info(
				'IntelligentKeyboardSection:checkVoipHotkeyFeatureCapability:Then',
				value
			);
			const type = 'voipHotkeyFeature';
			next(value, type);
		});
	}

	private checkKeyboardBacklightFeatureCapability(next: (value: boolean, type: string) => void) {
		this.logger.info(
			'IntelligentKeyboardSection:checkKeyboardBacklightFeatureCapability:Before'
		);
		this.keyboardBacklightFeature.getCapabililty().then((value) => {
			this.logger.info(
				'IntelligentKeyboardSection:checkKeyboardBacklightFeatureCapability:Then',
				value
			);
			const type = 'keyboardBacklightFeature';
			next(value, type);
		});
	}

	private checkTopRowFunctionFeatureCapability(next: (value: boolean, type: string) => void) {
		this.logger.info('IntelligentKeyboardSection:checkTopRowFunctionFeatureCapability:Before');
		this.topRowFunctionFeature.getCapability().then((value) => {
			this.logger.info(
				'IntelligentKeyboardSection:checkTopRowFunctionFeatureCapability:Then',
				value
			);
			const type = 'topRowFunctionFeature';
			next(value, type);
		});
	}

	private checkFnCtrlKeySwapFeature(next: (value: boolean, type: string) => void) {
		this.logger.info('FnCtrlKeySwapFeature:checkFnCtrlKeySwapFeature:Before');
		this.fnCtrlKeySwapFeature.getCapabililty().then((value) => {
			this.logger.info('FnCtrlKeySwapFeature:checkFnCtrlKeySwapFeature:Then', value);
			const type = 'fnCtrlKeySwapFeature';
			next(value, type);
		});
	}

	private checkUserDefinedKeyFeature(next: (value: boolean, type: string) => void) {
		this.logger.info('UserDefinedKey:checkUserDefinedKey:Before');
		this.userDefinedKey.getCapabililty().then((value) => {
			this.logger.info('UserDefinedKey:checkUserDefinedKey:Then', value);
			const type = 'userDefinedKey';
			next(value, type);
		});
	}
}
